import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userNeedsEmailVerification } from '../services/auth';
import { readDisplayNameOnboardingDoneCache } from '../services/displayNameOnboardingCache';
import { shouldPromptDisplayNameOnboarding } from '../utils/shouldPromptDisplayNameOnboarding';
import { safeInternalPath } from '../utils/safeInternalPath';
import { RouteFallback } from './RouteFallback';
import { AppPinLockGate } from './app-pin/AppPinLockGate';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const CONFIRM_NAME_PATH = '/confirm-display-name';

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { currentUser, loading, postGoogleLegalNavGate, userData, userDocReady, userProfileUnavailable } = useAuth() as {
    currentUser: {
      uid: string;
      emailVerified: boolean;
      displayName?: string | null;
      providerData: { providerId: string }[];
    } | null;
    loading: boolean;
    postGoogleLegalNavGate: { ready: boolean; needsConsent: boolean };
    userData: { displayName?: string | null; displayNameOnboardingDone?: boolean } | null;
    userDocReady: boolean;
    userProfileUnavailable: boolean;
  };
  const location = useLocation();

  if (loading) {
    return null;
  }

  if (!currentUser) {
    const invite = new URLSearchParams(location.search).get('invite')?.trim();
    if (invite) {
      return <Navigate to={`/?invite=${encodeURIComponent(invite)}`} replace />;
    }
    return <Navigate to="/" replace />;
  }

  if (userNeedsEmailVerification(currentUser)) {
    return <Navigate to={`/verify-email${location.search}`} replace />;
  }

  if (!postGoogleLegalNavGate.ready) {
    return <RouteFallback />;
  }

  if (postGoogleLegalNavGate.needsConsent) {
    const invite = new URLSearchParams(location.search).get('invite')?.trim();
    const suffix = invite ? `?invite=${encodeURIComponent(invite)}` : '';
    return <Navigate to={`/${suffix}`} replace />;
  }

  if (!userDocReady) {
    return <RouteFallback />;
  }

  const cachedDone = readDisplayNameOnboardingDoneCache(currentUser.uid);
  const needsDisplayNameOnboarding = shouldPromptDisplayNameOnboarding({
    userDocReady,
    displayNameOnboardingDone: userData?.displayNameOnboardingDone,
    cachedDone,
    profileUnavailable: userProfileUnavailable,
    hasDisplayName: Boolean(
      (typeof userData?.displayName === 'string' && userData.displayName.trim()) ||
        currentUser.displayName?.trim()
    ),
  });

  if (needsDisplayNameOnboarding && location.pathname !== CONFIRM_NAME_PATH) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`${CONFIRM_NAME_PATH}?next=${next}`} replace />;
  }

  if (location.pathname === CONFIRM_NAME_PATH && !needsDisplayNameOnboarding) {
    const next = safeInternalPath(new URLSearchParams(location.search).get('next'));
    return <Navigate to={next} replace />;
  }

  if (location.pathname === CONFIRM_NAME_PATH) {
    return <>{children}</>;
  }

  return <AppPinLockGate>{children}</AppPinLockGate>;
}
